import {
    ChangeEvent,
    FC,
    FocusEvent,
    FormEvent,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import _ from 'lodash';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { getPurpurColor } from '@telialt/common-theme/src/purpurSelectors';
import { isDefined } from '@telialt/utils/src/helpers';
import { useComponentId } from '@telialt/utils/src/hooks';

import {
    FromInputRange,
    InputRangesContainer,
    RangeTextField,
    TextFieldsContainer,
    ToInputRange,
} from './RangeSlider.styles';

type Props = {
    minValue: number;
    maxValue: number;
    fromValue?: number;
    toValue?: number;
    step?: number;
    ariaLabel?: string;
    fromLabel?: string;
    toLabel?: string;
    inputAdornment?: ReactNode;
    dataTest?: string;
    onChange?: (value: [number, number]) => void;
    formatLabel?: (value: number) => string;
    parseLabel?: (label: string) => number;
    debounceTime?: number;
};

const defaultFormatLabel = (value: number) => value.toString();
const defaultParseLabel = (label: string) =>
    /^[0-9\.]+$/.test(label.replace(',', '.')) ? parseFloat(label.replace(',', '.')) : undefined;

const DEFAULT_CHANGE_EVENT_DEBOUNCE_TIME = 500;
const INPUT_RANGE_UPDATE_DEBOUNCE_TIME = 500;

export const RangeSlider: FC<Props> = ({
    minValue,
    maxValue,
    fromValue: fromValueParam,
    toValue: toValueParam,
    step,
    ariaLabel,
    fromLabel,
    toLabel,
    inputAdornment,
    dataTest,
    onChange,
    formatLabel = defaultFormatLabel,
    parseLabel = defaultParseLabel,
    debounceTime = DEFAULT_CHANGE_EVENT_DEBOUNCE_TIME,
}) => {
    const { t } = useTranslations();

    const [firstValue, setFirstValue] = useState(fromValueParam || minValue);
    const [secondValue, setSecondValue] = useState(toValueParam || maxValue);

    const fromValue = Math.min(firstValue, secondValue);
    const toValue = Math.max(firstValue, secondValue);

    const [fromInputValue, setFromInputValue] = useState(formatLabel(fromValue));
    const [toInputValue, setToInputValue] = useState(formatLabel(toValue));

    const fromRangeId = useComponentId();
    const toRangeId = useComponentId();

    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const formatLabelRef = useRef(formatLabel);
    formatLabelRef.current = formatLabel;
    const parseLabelRef = useRef(parseLabel);
    parseLabelRef.current = parseLabel;
    const firstValueRef = useRef(firstValue);
    firstValueRef.current = firstValue;
    const secondValueRef = useRef(secondValue);
    secondValueRef.current = secondValue;
    const fromValueRef = useRef(fromValue);
    fromValueRef.current = fromValue;
    const toValueRef = useRef(toValue);
    toValueRef.current = toValue;

    const debouncedOnChange = useMemo(
        () => (onChangeRef.current ? _.debounce((range) => onChangeRef.current?.(range), debounceTime) : undefined),
        [debounceTime],
    );

    // Handle new range value being set from outside
    useEffect(() => {
        if (!isDefined(fromValueParam) || !isDefined(toValueParam)) {
            return;
        }

        if (
            fromValueParam >= minValue &&
            fromValueParam <= toValueParam &&
            toValueParam <= maxValue &&
            toValueParam >= fromValueParam
        ) {
            setFirstValue(fromValueParam);
            setFromInputValue(formatLabelRef.current(fromValueParam));
            setSecondValue(toValueParam);
            setToInputValue(formatLabelRef.current(toValueParam));
        }
    }, [fromValueParam, maxValue, minValue, toValueParam]);

    // Handle new min or max value being set from outside
    useEffect(() => {
        if (fromValue < minValue && toValue > maxValue) {
            setFirstValue(minValue);
            setFromInputValue(formatLabelRef.current(minValue));
            setSecondValue(maxValue);
            setToInputValue(formatLabelRef.current(maxValue));
        }
    }, [fromValue, maxValue, minValue, toValue]);

    // Update selected values if nothing is passed as params and min or max changes
    useEffect(() => {
        if (!isDefined(fromValueParam) && !isDefined(toValueParam)) {
            setFirstValue(minValue);
            setFromInputValue(formatLabelRef.current(minValue));
            setSecondValue(maxValue);
            setToInputValue(formatLabelRef.current(maxValue));
        }
    }, [fromValueParam, maxValue, minValue, toValueParam]);

    const handleSliderRangeChange = (rangeInput: 'first' | 'second', event: FormEvent<HTMLInputElement>) => {
        if (!(event.target instanceof HTMLInputElement)) {
            return;
        }
        const parsedValue = parseFloat(event.target.value);
        if (parsedValue < minValue || parsedValue > maxValue) {
            return;
        }

        if (rangeInput === 'first') {
            setFirstValue(parsedValue);
            const newFromValue = Math.min(parsedValue, secondValue);
            const newToValue = Math.max(parsedValue, secondValue);
            setFromInputValue(formatLabel(newFromValue));
            setToInputValue(formatLabel(newToValue));
            debouncedOnChange?.([newFromValue, newToValue]);
        } else if (rangeInput === 'second') {
            setSecondValue(parsedValue);
            const newFromValue = Math.min(firstValue, parsedValue);
            const newToValue = Math.max(firstValue, parsedValue);
            setFromInputValue(formatLabel(newFromValue));
            setToInputValue(formatLabel(newToValue));
            debouncedOnChange?.([newFromValue, newToValue]);
        }
    };

    const updateRangeFromInput = useCallback(
        (side: 'from' | 'to', inputValue: string, resetOnInvalid?: boolean, hasDebounce = true) => {
            const parsedValue = parseLabelRef.current(inputValue);

            const isInvalid =
                !_.isNumber(parsedValue) ||
                !_.isFinite(parsedValue) ||
                (side === 'from' && parsedValue < minValue) ||
                (side === 'from' && parsedValue > toValueRef.current) ||
                (side === 'to' && parsedValue > maxValue) ||
                (side === 'to' && parsedValue < fromValueRef.current);

            const setRangeValues = (range: [number, number]) => {
                const isDifferent = range[0] !== fromValueRef.current || range[1] !== toValueRef.current;
                if (isDifferent) {
                    (hasDebounce ? debouncedOnChange : onChangeRef.current)?.(range);
                }
            };
            const resetRangeLimits = () => {
                if (side === 'from') {
                    setFromInputValue(formatLabelRef.current(fromValueRef.current));
                } else if (side === 'to') {
                    setToInputValue(formatLabelRef.current(toValueRef.current));
                }
            };

            if (isInvalid) {
                if (resetOnInvalid) {
                    resetRangeLimits();
                }
            } else if (side === 'from') {
                (firstValueRef.current < secondValueRef.current ? setFirstValue : setSecondValue)(parsedValue);
                setRangeValues([parsedValue, toValueRef.current]);
            } else if (side === 'to') {
                (firstValueRef.current > secondValueRef.current ? setFirstValue : setSecondValue)(parsedValue);
                setRangeValues([fromValueRef.current, parsedValue]);
            }
        },
        [debouncedOnChange, maxValue, minValue],
    );

    const debouncedUpdateRangeFromInput = useMemo(
        () => _.debounce(updateRangeFromInput, INPUT_RANGE_UPDATE_DEBOUNCE_TIME),
        [updateRangeFromInput],
    );

    const handleInputChange = (side: 'from' | 'to', event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (side === 'from') {
            setFromInputValue(value);
        } else if (side === 'to') {
            setToInputValue(value);
        }
        debouncedUpdateRangeFromInput(side, value);
    };

    const handleInputBlur = (side: 'from' | 'to', event: FocusEvent<HTMLInputElement>) => {
        const value = event.target.value;
        updateRangeFromInput(side, value, true, false);
    };

    const getTrackGradient = () => {
        const fullWidth = maxValue - minValue;

        return `linear-gradient(
            to right,
            ${getPurpurColor('gray400')} 0%,
            ${getPurpurColor('gray400')} ${((fromValue - minValue) / fullWidth) * 100}%,
            ${getPurpurColor('borderInteractivePrimary')} ${((fromValue - minValue) / fullWidth) * 100}%,
            ${getPurpurColor('borderInteractivePrimary')} ${((toValue - minValue) / fullWidth) * 100}%,
            ${getPurpurColor('gray400')} ${((toValue - minValue) / fullWidth) * 100}%,
            ${getPurpurColor('gray400')} 100%)
        `;
    };

    return (
        <div data-test={dataTest}>
            <TextFieldsContainer>
                <RangeTextField
                    endAdornment={inputAdornment}
                    value={fromInputValue}
                    label={fromLabel || t['common.range_slider.label_from']}
                    inputMode="numeric"
                    onChange={(event) => handleInputChange('from', event)}
                    onBlur={(event) => handleInputBlur('from', event)}
                    data-test={dataTest ? `${dataTest}-input-from` : undefined}
                />
                <RangeTextField
                    endAdornment={inputAdornment}
                    value={toInputValue}
                    label={toLabel || t['common.range_slider.label_to']}
                    inputMode="numeric"
                    onChange={(event) => handleInputChange('to', event)}
                    onBlur={(event) => handleInputBlur('to', event)}
                    data-test={dataTest ? `${dataTest}-input-to` : undefined}
                />
            </TextFieldsContainer>
            <InputRangesContainer>
                <FromInputRange
                    id={fromRangeId}
                    value={fromValue}
                    min={minValue}
                    max={maxValue}
                    step={step}
                    onInput={(event) => handleSliderRangeChange('first', event)}
                    aria-label={[ariaLabel, t['common.range_slider.label_from']].filter(Boolean).join(' - ')}
                    aria-valuetext={formatLabel(fromValue)}
                    data-test={dataTest ? `${dataTest}-from` : undefined}
                />
                <ToInputRange
                    id={toRangeId}
                    value={toValue}
                    min={minValue}
                    max={maxValue}
                    step={step}
                    onInput={(event) => handleSliderRangeChange('second', event)}
                    style={{ background: getTrackGradient() }}
                    aria-label={[ariaLabel, t['common.range_slider.label_to']].filter(Boolean).join(' - ')}
                    aria-valuetext={formatLabel(toValue)}
                    data-test={dataTest ? `${dataTest}-to` : undefined}
                />
            </InputRangesContainer>
        </div>
    );
};
